












































































































































































































































































import {
  computed,
  defineComponent,
  getCurrentInstance,
  Ref,
  ref,
  toRefs,
  toRef,
  PropType,
  watch,
} from '@vue/composition-api';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  HelpCircleIcon,
  SearchIcon,
  LoadingIcon,
  CheckCircleIcon,
  ErrorCircleIcon,
  UsergroupIcon,
  UserIcon,
  SwapIcon,
  AddCircleIcon,
} from 'tdesign-icons-vue';
import _ from 'lodash';

import CoLevelHelp, { helpHandler } from '@/renderer/components/dialog/AddTransfer/components/coLevelHelp.vue';
import TemporaryMemberList, {
  TemporaryMemberListRef,
} from '@/renderer/components/dialog/AddTransfer/AddMember/components/TemporaryMemberList.vue';
import MemberTable, {
  MemberTableRef,
} from '@/renderer/components/dialog/AddTransfer/AddMember/components/MemberTable/index.vue';

import { i18n_t } from '@/locales/i18n';
import tips from '@/renderer/scripts/utils/tips';
import { checkRoleIsAdmin } from '@/renderer/scripts/utils/UserRole';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';
import { isAdminOrSuperAdmin } from '@/renderer/scripts/utils/UserRole/getters';

import { showPhoneOrAccountName } from '@/renderer/components/dialog/AddTransfer/compositions/useTools';
import useOrgTreeStructure from '@/renderer/components/dialog/AddTransfer/compositions/useOrgTreeStructure';
import searchHandler from '@/renderer/components/dialog/AddTransfer/compositions/searchHandler';
import useOperation from '@/renderer/components/dialog/AddTransfer/AddMember/compositions/useOperation';
import useMembers, { SpaceInfoProp } from '@/renderer/components/dialog/AddTransfer/AddMember/compositions/useMembers';

export interface SubmitEvent {
  changed?: CoFile.TeamOrMemberItem[];
  deleted?: CoFile.TeamOrMemberItem[];
  added?: CoFile.TeamOrMemberItem[];
}

export default defineComponent({
  name: 'AddMember',
  components: {
    HelpCircleIcon,
    SearchIcon,
    LoadingIcon,
    CheckCircleIcon,
    ErrorCircleIcon,
    UsergroupIcon,
    UserIcon,
    SwapIcon,
    CoLevelHelp,
    ChevronDownIcon,
    ChevronRightIcon,
    AddCircleIcon,
    TemporaryMemberList,
    MemberTable,
  },
  props: {
    show: {
      required: true,
      type: Boolean,
      default: false,
    },
    attach: {
      type: String,
      default: 'body',
    },
    /**
     * @description: 给什么类型的空间添加成员
     * team: 团队空间 group: 协作空间 other: 其他(例如收集添加成员)
     */
    spaceInfo: {
      required: true,
      type: Object as PropType<SpaceInfoProp>,
    },
    /** 添加时默认设置的权限 */
    defaultRoleId: {
      type: Number,
    },
    /** 精简模式：无法进行权限修改操作 */
    simpleMode: {
      type: Boolean,
      default: false,
    },
    /** 已添加的企业空间用户[企业管理] */
    originPermission: {
      required: false,
      type: Array as PropType<CoFile.TeamOrMemberItem[]>,
      default: () => [],
    },
    /** 放开全部身份成员的角色选择（即不禁用管理员选择角色） */
    noDisableRoleSelect: {
      type: Boolean,
      default: false,
    },
    /** 是否允许操作未激活的用户 */
    allowInactiveUser: {
      type: Boolean,
      default: false,
    },
    /** 是否允许对自己授权 */
    canAuthorizeSelf: {
      type: Boolean,
      default: false,
    },
    /** 是否展示全手机号 (为false则要给手机号打码) */
    showPhoneNumber: {
      type: Boolean,
      default: false,
    },
    /** 展示临时用户 */
    showTemporaryMember: {
      type: Boolean,
      default: false,
    },
    /** 是否允许管理员设置权限 */
    canAdminSetRole: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const vm = getCurrentInstance()?.proxy || context.root;
    const store = vm.$store;

    const {
      allowInactiveUser,
      showPhoneNumber,
      defaultRoleId: teamDefaultRoleId,
      originPermission,
      spaceInfo,
    } = toRefs(props);

    const otherTypeSearchKey = ref(''); // 当空间类型为 other 时，缓存其搜索关键字
    const tabsValue = ref('member');
    const temporaryMemberListRef = ref<TemporaryMemberListRef | undefined>();
    const temporaryLabel = computed(() => {
      if (tabsValue.value === 'temporaryMember') {
        return i18n_t('临时成员（{slot1}）', {
          slot1: temporaryMemberListRef.value?.total || 0,
        });
      }
      return i18n_t('临时成员');
    });
    const memberTableRef = ref<MemberTableRef | undefined>();
    const selectedNum = computed(() => memberTableRef.value?.selectedNum || 0);

    const routeName = computed(() => vm.$route.name as string);
    const innerShow: Ref<boolean> = computed({
      get() {
        return props.show;
      },
      set(val) {
        context.emit('update:show', val);
      },
    });
    const isOnlySearchUser = computed(() => !store.getters.enableViewAllOrgUser && !isAdminOrSuperAdmin.value);

    const tip = computed(() => i18n_t('Special.成员'));

    const {
      treeKeys,
      rootTeamId,
      teamData,
      hasMoreUser,
      loadLimit,
      loading: treeLoading,
      methods: treeMethods,
    } = useOrgTreeStructure({
      showPhoneNumber: showPhoneNumber.value,
    });

    const {
      loading: memberLoading,
      asyncLoading: memberAsyncLoading,
      memberData,
      methods: memberMethods,
    } = useMembers(spaceInfo);

    const showTotalText = computed(() => {
      const { type } = spaceInfo.value;
      /** 文件收集 || 已经加载全部成员 时显示“全部成员文案 */
      return type === 'other' || !memberData.hasMore;
    });

    const {
      addedMembers,
      addedMembersSet,
      originAddedMembersSet,
      methods: operationMethods,
    } = useOperation({
      spaceType: computed(() => spaceInfo.value.type),
      defaultRoleId: teamDefaultRoleId,
      allowInactiveUser,
      originAddedMembers: originPermission,
      getTeamMemberTree: treeMethods.getTeamMemberTree,
    });

    const {
      teamData: searchTeamData,
      memberData: searchMemberData,
      loadingStatus,
      loading: searchLoading,
      searchKey,
      collapseExpand: searchCollapseExpand,
      methods: searchMethods,
    } = searchHandler({
      onlySearchUser: isOnlySearchUser.value,
      showPhoneNumber: showPhoneNumber.value,
    });

    const { show: showHelp, openHelp } = helpHandler();

    const renderPermissionsList = computed(() => {
      // 姓名或手机号 搜索
      const filterPermissionList = addedMembers.value
        .filter(item => {
          if (spaceInfo.value.type === 'other') {
            return item?.name?.toLowerCase().includes(otherTypeSearchKey.value.toLowerCase());
          }
          return true;
        })
        .sort((a, b) => {
          // 不支持操作的排序到最前
          if (a.disabledOperate && !b.disabledOperate) return -1;
          return 1;
        });
      return filterPermissionList;
    });

    const confirm = (callback: Function) => {
      const dialog = CustomDialog.confirm({
        header: i18n_t('提示'),
        body: i18n_t('操作未提交，确定要关闭窗口吗？'),
        theme: 'warning',
        onConfirm: () => {
          if (dialog.hide) {
            dialog.hide();
            callback();
          }
        },
      });
    };

    const methods = {
      initData() {
        /** 初始化左侧树 */
        treeMethods.initData();
        /** 初始化右侧成员 */
        methods.getMembers('', true);
      },
      /**
       * @description 过滤已改变的权限列表及被删除的权限列表
       * @return {,changed,deleted}
       */
      filterPermisionList() {
        const changed: CoFile.TeamOrMemberItem[] = []; // 改变的授权（包含新增）
        const deleted: CoFile.TeamOrMemberItem[] = []; // 删除的授权
        let added: CoFile.TeamOrMemberItem[] = []; // 新增的授权
        const originPermission = memberData.list;
        const permissionList = addedMembers.value;

        // FIX: 由于自己无法对自己授权，因此授权时要将本人的权限过滤掉 (如果有传入 canAuthorizeSelf，则允许对自己授权)
        const currUserId = vm.$store.getters.userId;
        const originPermissionList = originPermission.filter(
          item => item.userId !== currUserId || props.canAuthorizeSelf,
        );
        const currPermissionList = permissionList.filter(item => item.userId !== currUserId || props.canAuthorizeSelf);
        if (!props.simpleMode) {
          // 过滤出已被删除的权限
          originPermissionList.forEach(currItem => {
            const currItemId = currItem.spaceId || currItem.userId;
            const index = currPermissionList.findIndex(item => {
              const itemId = item.spaceId || item.userId;
              return currItemId === itemId;
            });
            if (index === -1) {
              // 说明原始选择已被删除
              deleted.push(currItem);
            }
          });

          // 过滤出被修改（包含新增）的权限）
          currPermissionList.forEach(currItem => {
            const currItemId = currItem.spaceId || currItem.userId;
            const index = originPermissionList.findIndex(item => {
              const itemId = item.spaceId || item.userId;
              return currItemId === itemId;
            });
            if (index > -1) {
              const item = originPermissionList[index];
              item.roleId !== currItem.roleId && changed.push(currItem);
            } else {
              added.push(currItem);
            }
          });
        } else {
          added = currPermissionList;
        }
        return {
          changed,
          deleted,
          added,
        };
      },
      onConfirm() {
        const res = methods.filterPermisionList();
        const noChanged = res.added.length === 0 && res.changed.length === 0 && res.deleted.length === 0;
        if (selectedNum.value && noChanged) {
          tips.error(i18n_t('选择成员不可被修改'));
          return;
        }
        if (noChanged) {
          tips.error(
            i18n_t('请选择至少一个{slot1}', {
              slot1: tip.value,
            }),
          );
          return;
        }
        if (!noChanged) {
          context.emit('onSubmit', res);
        } else {
          innerShow.value = false;
        }
      },
      onClose() {
        const res = methods.filterPermisionList();
        if (res.changed.length > 0 || res.deleted.length > 0) {
          confirm(() => {
            innerShow.value = false;
          });
        } else {
          innerShow.value = false;
        }
      },

      /** 穿梭框右侧成员删除 */
      onRemoveMember(index: number, item: CoFile.TeamOrMemberItem) {
        if (props.simpleMode) {
          operationMethods.removeTeamOrMember(index, item);
          return;
        }
        const dialog = CustomDialog.confirm({
          theme: 'warning',
          header: i18n_t('确定要移除"{slot1}"吗？', {
            slot1: item.name,
          }),
          body: i18n_t('移除后，该用户在空间中创建的文件不会被删除'),
          async onConfirm() {
            if (dialog.hide) {
              dialog.hide();
            }
            operationMethods.removeTeamOrMember(index, item);
          },
        });
      },
      /** 穿梭框右侧成员批量删除 */
      onRemoveMembers() {
        if (props.simpleMode) {
          operationMethods.removeTeamOrMembers();
          memberTableRef.value?.removeSelected();
          return;
        }
        const dialog = CustomDialog.confirm({
          theme: 'warning',
          header: i18n_t('确定要移除所选{slot1}个成员吗？', {
            slot1: selectedNum.value,
          }),
          body: i18n_t('移除后，该用户在空间中创建的文件不会被删除'),
          async onConfirm() {
            if (dialog.hide) {
              dialog.hide();
            }
            operationMethods.removeTeamOrMembers();
            memberTableRef.value?.removeSelected();
          },
        });
      },
      onSearchTeamsOrMembers(value: string) {
        searchMethods.onSearchTeamsOrMembers(value, rootTeamId.value);
      },
      /** 切换临时成员 or 企业内正式成员 */
      tabsChange(val: string) {
        tabsValue.value = val;
        if (val === 'member') {
          searchMethods.resetData();
          searchKey.teamOrMemberCache = '';
        }
      },
      /** 穿梭框右侧成员选中状态改变 */
      onSelectMemberChange(selectedAuthList: number[]) {
        addedMembers.value.forEach((item: any) => {
          item.checked = selectedAuthList.includes(item.uid);
        });
      },
      /** 穿梭框右侧成员权限改变 */
      onRoleChange(roleId: number, uid: string) {
        const [item] = addedMembers.value.filter(item => uid === item.uid);
        if (item) {
          item.roleId = roleId;
        }
      },
      /** 穿梭框右侧成员权限批量改变 */
      onBatchMemberRoleChange(roleId: number) {
        operationMethods.changeTeamOrMembersPermission(roleId, props.noDisableRoleSelect);
      },
      /** 穿梭框左侧成员添加状态判断 */
      addedStatus(item: CoFile.TeamOrMemberItem) {
        let status = '';
        const treeId = item.userId;
        if (treeId) {
          if (originAddedMembersSet.has(treeId) && addedMembersSet.has(treeId)) {
            status = 'originAdd';
          } else if (addedMembersSet.has(treeId)) {
            status = 'newAdd';
          }
        }
        return status;
      },
      /** 获取穿梭框左侧成员信息 */
      async getMembers(searchKey?: string, isInit = false) {
        if (spaceInfo.value.type !== 'other') {
          if (isInit) memberMethods.resetData();
          await memberMethods.getMembers({ keyword: searchKey });
          operationMethods.initOriginAdded(memberData.list);
        } else {
          if (typeof searchKey !== 'undefined') {
            otherTypeSearchKey.value = searchKey;
          }
          // 使用从外面传入的原始数据
          operationMethods.initOriginAdded(props.originPermission);
        }
      },
    };

    methods.initData();

    // 监听右侧列表变化后 重新标记左侧节点的选中态
    watch(
      () => memberData.list,
      (val, old) => {
        if (val.length !== old.length && innerShow.value) {
          operationMethods.initOriginAdded(val);
        }
      },
    );

    return {
      innerShow,
      isOnlySearchUser,
      tip,
      operationMethods,
      ...methods,

      /** 功能函数 */
      checkRoleIsAdmin,
      showPhoneOrAccountName,

      /** 帮助 */
      showHelp,
      openHelp,

      /** 穿梭框左侧信息加载 */
      tabsValue,
      treeKeys,
      treeLoading,
      teamTree: toRef(teamData, 'teamTree'),
      rootTeamId,
      loadLimit,
      hasMoreUser,
      treeMethods,
      /** 临时成员 */
      temporaryLabel,
      temporaryMemberListRef,
      /** 穿梭框左侧搜索 */
      loadingStatus,
      searchLoading,
      searchTeamData,
      searchMemberData,
      searchKey,
      searchCollapseExpand,
      searchMethods,

      /** 穿梭框右侧内数据加载 */
      memberTableRef,
      selectedNum,
      showTotalText,
      memberLoading,
      memberAsyncLoading,
      addedMembers,
      renderPermissionsList,
    };
  },
});
